import { matchVenueDate } from "./matchTime";
import React, { useEffect, useMemo, useState } from "react";
import { CRS, divIcon, latLng } from "leaflet";
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import { Link } from "react-router-dom";
import "leaflet/dist/leaflet.css";
import { getMatchCoordinates } from "./matchCoordinates";
import { DISCIPLINES, matchDateLabel, matchLocation } from "./matchDiscovery";
import { parseCalendarDate } from "../../utils/date";
import { groupRelatedMatches, sideMatchLabel } from "./relatedMatchDiscovery";
export { getMatchCoordinates } from "./matchCoordinates";

const DEFAULT_CENTER = [39.8283, -98.5795];
const DEFAULT_ZOOM = 4;
const SINGLE_MATCH_ZOOM = 11;
const defaultMatchId = (match) => match?.matchId;
const datePinLabel = (match) => matchVenueDate(match)
  ?.toLocaleDateString("en-US", { month: "short", day: "numeric" }) || "Date TBA";

function MapViewportController({ points, selectedPoint, singlePointMode, onBoundsChange, onZoomChange, dateMarkers }) {
  const map = useMap();

  useEffect(() => {
    const reportZoom = () => onZoomChange(map.getZoom());
    map.on("zoomend", reportZoom);
    reportZoom();
    return () => map.off("zoomend", reportZoom);
  }, [map, onZoomChange]);

  useEffect(() => {
    if (!onBoundsChange) return undefined;
    const reportBounds = () => {
      const bounds = map.getBounds();
      onBoundsChange({ north: bounds.getNorth(), south: bounds.getSouth(), east: bounds.getEast(), west: bounds.getWest() });
    };
    map.on("moveend", reportBounds);
    reportBounds();
    return () => map.off("moveend", reportBounds);
  }, [map, onBoundsChange]);

  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return undefined;
    let frame;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      // Preserve the geographic center when a hidden tab becomes visible or resizes.
      frame = requestAnimationFrame(() => map.invalidateSize({ pan: true, animate: false }));
    });
    observer.observe(map.getContainer());
    return () => { observer.disconnect(); cancelAnimationFrame(frame); };
  }, [map]);

  useEffect(() => {
    if (!points.length) {
      map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
      return;
    }

    if (singlePointMode) {
      const point = points[0] || null;
      if (!point) {
        map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
        return;
      }
      map.setView([point.latitude, point.longitude], SINGLE_MATCH_ZOOM, {
        animate: false
      });
      return;
    }

    const bounds = points.map((point) => [point.latitude, point.longitude]);
    map.fitBounds(bounds, {
      padding: dateMarkers ? [64, 50] : [36, 36],
      maxZoom: points.length === 1 ? SINGLE_MATCH_ZOOM : 8,
      animate: false
    });
  }, [map, points, singlePointMode, dateMarkers]);

  useEffect(() => {
    if (!selectedPoint || singlePointMode) {
      return;
    }

    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    map.flyTo([selectedPoint.latitude, selectedPoint.longitude], Math.max(map.getZoom(), 7), {
      duration: reduceMotion ? 0 : 0.45,
      animate: !reduceMotion
    });
  }, [map, selectedPoint, singlePointMode]);

  return null;
}

function MatchLocationMap({
  matches,
  selectedMatchId,
  onSelectMatch,
  renderPopupContent,
  emptyMessage = "No map data is available for these matches.",
  mapLabel = "Match location map",
  className = "",
  showWhenEmpty = false,
  dateMarkers = false,
  scrollWheelZoom = true,
  getMatchId = defaultMatchId,
  onBoundsChange
}) {
  const [tileError, setTileError] = useState(false);
  const [mapZoom, setMapZoom] = useState(DEFAULT_ZOOM);
  const tileEventHandlers = useMemo(() => ({
    tileerror: () => setTileError(true)
  }), []);

  const defaultPinIcon = useMemo(
    () =>
      divIcon({
        className: "match-location-pin-icon",
        html: '<span class="match-location-pin"><span class="match-location-pin-core"></span></span>',
        iconSize: [26, 38],
        iconAnchor: [13, 34],
        popupAnchor: [0, -28]
      }),
    []
  );

  const selectedPinIcon = useMemo(
    () =>
      divIcon({
        className: "match-location-pin-icon",
        html: '<span class="match-location-pin selected"><span class="match-location-pin-core"></span></span>',
        iconSize: [30, 44],
        iconAnchor: [15, 39],
        popupAnchor: [0, -32]
      }),
    []
  );

  const mappedMatches = useMemo(
    () =>
      (Array.isArray(matches) ? matches : [])
        .map((match) => {
          const coordinates = getMatchCoordinates(match);
          return coordinates ? { match, ...coordinates } : null;
        })
        .filter(Boolean),
    [matches]
  );

  const selectedPoint = useMemo(
    () => {
      if (!selectedMatchId) {
        return null;
      }
      return mappedMatches.find((item) => getMatchId(item.match) === selectedMatchId) || null;
    },
    [mappedMatches, selectedMatchId, getMatchId]
  );

  // Group pins that would overlap at the current zoom, including shared venues.
  const markerGroups = useMemo(() => {
    const groups = [];
    // A stable geographic order keeps cluster locations steady when the list is sorted.
    const ordered = [...mappedMatches].sort((a, b) => a.longitude - b.longitude || a.latitude - b.latitude
      || String(getMatchId(a.match)).localeCompare(String(getMatchId(b.match))));
    ordered.forEach((item) => {
      const pixel = CRS.EPSG3857.latLngToPoint(latLng(item.latitude, item.longitude), mapZoom);
      const group = dateMarkers && groups.find((entries) => Math.abs(entries[0].pixel.x - pixel.x) < 108
        && Math.abs(entries[0].pixel.y - pixel.y) < 48);
      const point = { ...item, pixel };
      if (group) group.push(point);
      else groups.push([point]);
    });
    return groups;
  }, [mappedMatches, dateMarkers, mapZoom, getMatchId]);

  const dateIcons = useMemo(() => dateMarkers ? markerGroups.map((group) => {
    const selected = group.find((item) => getMatchId(item.match) === selectedMatchId);
    const clustered = group.length > 1;
    const label = selected && clustered ? `${datePinLabel(selected.match)} · +${group.length - 1}` : clustered ? `${group.length} matches` : datePinLabel(group[0].match);
    const width = selected && clustered ? 126 : clustered ? Math.max(100, 84 + String(group.length).length * 8) : 80;
    return divIcon({
      className: "discovery-map-marker",
      html: `<span class="discovery-pin${clustered ? " is-cluster" : ""}${selected ? " is-selected" : ""}"><span>${label}</span></span>`,
      iconSize: [width, 44], iconAnchor: [width / 2, 42], popupAnchor: [0, -38],
    });
  }) : [], [markerGroups, dateMarkers, selectedMatchId, getMatchId]);

  if ((!showWhenEmpty && mappedMatches.length === 0) || tileError) {
    const failedToLoad = tileError;
    return (
      <div className={`match-location-map-empty ${className}`.trim()}>
        <div role="status">
          <strong>{failedToLoad ? "Map temporarily unavailable" : "No map available"}</strong>
          <div className="leaderboard-meta">
            {failedToLoad ? "The map could not load. Match details are still available on this page." : emptyMessage}
          </div>
        </div>
        {failedToLoad && (
          <div>
            <button type="button" className="match-search-button" onClick={() => setTileError(false)}>
              Retry map
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`match-location-map-shell ${className}`.trim()}>
      <MapContainer
        className="match-location-map"
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        scrollWheelZoom={scrollWheelZoom}
        aria-label={mapLabel}
      >
        <TileLayer
          attribution={'&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors | Place search: <a href="https://docs.aws.amazon.com/location/latest/developerguide/data-attribution.html">Amazon Location</a>'}
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          // Tile requests need the site origin even when the page uses a same-origin referrer policy.
          referrerPolicy="strict-origin-when-cross-origin"
          eventHandlers={tileEventHandlers}
        />

        <MapViewportController
          points={mappedMatches}
          selectedPoint={selectedPoint}
          singlePointMode={mappedMatches.length === 1}
          onBoundsChange={onBoundsChange}
          onZoomChange={setMapZoom}
          dateMarkers={dateMarkers}
        />

        {markerGroups.map((group, index) => {
          const selected = group.find((point) => getMatchId(point.match) === selectedMatchId);
          const item = selected || group[0];
          const isSelected = Boolean(selected);
          const matchName = item.match?.matchName || "match";
          const groupLabel = `${group.length} matches ${group.every((point) => point.latitude === item.latitude && point.longitude === item.longitude) ? "at this venue" : "nearby"}`;

          return (
            <Marker
              key={JSON.stringify(group.map((point) => getMatchId(point.match) || `${point.latitude}-${point.longitude}`))}
              position={[group[0].latitude, group[0].longitude]}
              icon={dateMarkers ? dateIcons[index] : isSelected ? selectedPinIcon : defaultPinIcon}
              title={isSelected ? `Selected: ${matchName}${group.length > 1 ? ` · ${groupLabel}` : ""}` : group.length > 1 ? groupLabel : dateMarkers ? `${datePinLabel(item.match)} · ${matchName}` : matchName}
              alt={group.length > 1 ? groupLabel : `Map marker for ${matchName}`}
              zIndexOffset={isSelected ? 600 : 0}
              riseOnHover
              eventHandlers={{
                click: (event) => {
                  if (group.length === 1) onSelectMatch?.(getMatchId(item.match) || "");
                  // Leaflet toggles an already-open hover preview on click; keep it open after selection.
                  if (dateMarkers) event.target.openPopup();
                },
                mouseover: (event) => { if (dateMarkers && window.matchMedia?.("(hover: hover)").matches) event.target.openPopup(); }
              }}
            >
              {renderPopupContent && (
                <Popup minWidth={220} maxWidth={280}>
                  {group.length === 1 ? renderPopupContent(item.match) : <div className="discovery-venue-matches">
                    <strong>{groupLabel}</strong>
                    {groupRelatedMatches([...group].map(point => point.match).sort((a, b) => (parseCalendarDate(a.startDate || a.matchDate)?.getTime() ?? Infinity)
                      - (parseCalendarDate(b.startDate || b.matchDate)?.getTime() ?? Infinity))).flatMap(related => [...(related.mainMatch ? [related.mainMatch] : []), ...related.sideMatches]).map(match => <div className={`discovery-venue-match${match.parentMatchId ? " discovery-venue-side" : ""}${getMatchId(match) === selectedMatchId ? " is-selected" : ""}`} key={getMatchId(match)}>
                      <button type="button" onClick={() => onSelectMatch?.(getMatchId(match))}
                        aria-pressed={getMatchId(match) === selectedMatchId} aria-label={`Show ${match.matchName || "match"} in results`}>
                        {match.parentMatchId && <span>{sideMatchLabel(match)}</span>}
                        <strong>{match.matchName || "Match"}</strong>
                        <span>{matchDateLabel(match)}</span>
                        <span>{[DISCIPLINES[match.competitionType], matchLocation(match)].filter(Boolean).join(" · ")}</span>
                      </button>
                      {match.detailRoute && <Link to={match.detailRoute} aria-label={`View ${match.matchName || "match"}`}>View match →</Link>}
                    </div>)}
                  </div>}
                </Popup>
              )}
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
}

export default MatchLocationMap;
